import array from 'common/helpers/primitives/array'
import sentry from 'common/helpers/analytics/sentry'


interface IImageSizeProps {
  src: string
  w?: number
  h?: number
  x2?: boolean | string
  x3?: boolean | string
  srcSet?: string
  productId?: number
}

interface IModifiedImageSizeProps {
  src: string,
  srcSet?: string
}

const getLambdaSizes = (props: { src: string, w?: number, h?: number }): IModifiedImageSizeProps => {
  const { src, w, h } = props

  const key   = Boolean(w) ? 'w' : 'h'
  const value = w || h

  return { src: `${src}?${key}=${value}` }
}

const getLambdaSrcSet = (props: IImageSizeProps): IModifiedImageSizeProps => {
  if (props.src && props.srcSet) {
    return <IModifiedImageSizeProps>props
  }

  const { src, w, h, x3 } = props

  const maxSize = x3 ? 3 : 2
  const key     = Boolean(w) ? 'w' : 'h'
  const value   = w || h

  const srcSet = array.range(1, maxSize)
    .map((size, index) => (
      // image.png?w=200
      // image.png?w=400 2x
      // image.png?w=600 3x
      `${src}?${key}=${value * size}${index ? ` ${size}x` : ''}`
    ))
    .join(', ')

  return {
    ...getLambdaSizes(props),
    srcSet,
  }
}

const getSrcSet = (props: IImageSizeProps): IModifiedImageSizeProps => {
  const { src, x2, x3 } = props

  const sizes = [ x2, x3 ]
  let srcSet = src

  sizes.forEach((size, index) => {
    if (size) {
      // image.png -> image@2x.png
      const url = src.replace(/(\.[a-z]+)$/, `@${index + 2}x$1`)

      srcSet += `, ${url} ${index + 2}x`
    }
  })

  return { src, srcSet }
}

const modifyImageSizeProps = (props: IImageSizeProps): IModifiedImageSizeProps => {
  const { src, w, h, x2, x3, ...rest } = props

  if (!src) {
    sentry.captureException('Missed "src" value', {
      extra: {
        props,
        placement: 'ModifyImageSizeProps',
      },
    })

    return { src, ...rest }
  }

  const isSizeExist         = w || h
  const isMultiplierExist   = x2 || x3
  let result: IModifiedImageSizeProps

  if (isSizeExist && isMultiplierExist) {
    result = getLambdaSrcSet(props)
  }
  else if (isMultiplierExist) {
    result = getSrcSet(props)
  }
  else if (isSizeExist) {
    result = getLambdaSizes(props)
  }
  else {
    result = { src, ...rest }
  }

  return { ...result, ...rest }
}


export default modifyImageSizeProps
